import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductsPaginated, getBrands } from '../services/api';

const bgClasses = ['p1','p2','p3','p4','p5','p6'];
const defaultIcons = ['⚙️','🔩','🏗️','🔧','⚡','🏭'];

const Components = () => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState({ brand: '', search: '' });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });

  useEffect(() => {
    getBrands().then(data => {
      if (data) {
        setBrands(data);
      }
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { category: 'components', page, per_page: 12 };
    if (filters.search) params.search = filters.search;
    if (filters.brand) params.brand = filters.brand;
    getProductsPaginated(params).then(res => {
      const data = res?.data || [];
      if (data.length > 0) {
        const mapped = data.map((p, i) => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          cat: [p.category?.name, p.brand?.name, p.type_unit?.name].filter(Boolean).join(' · ') || 'Product',
          brand: p.brand?.name || null,
          stock: p.quantity || 0,
          image: p.primary_image_url || null,
          icon: defaultIcons[i % defaultIcons.length],
          bg: bgClasses[i % bgClasses.length],
        }));
        setProducts(mapped);
        if (!filters.search && !filters.brand && page === 1) {
        }
      } else {
        setProducts([]);
      }
      if (res?.meta) {
        setMeta(res.meta);
      }
      setLoading(false);
    });
  }, [filters, page]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );
    if (sectionRef.current) {
      const els = sectionRef.current.querySelectorAll('.rv');
      els.forEach(el => observer.observe(el));
      return () => els.forEach(el => observer.unobserve(el));
    }
  }, [products]);

  const handleInquire = (e, product) => {
    e.preventDefault();
    const message = `Hello AFS, I am interested in the following product:\nName Component: ${product.name}\nPlease send me more information. Thank you.`;
    sessionStorage.setItem('contact_prefill', JSON.stringify({
      subject: 'Product Inquiry',
      message,
    }));
    navigate('/contact');
  };

  return (
    <section id="components" ref={sectionRef}>
      <div className="wrap">
        <div className="comp-header rv">
          <div className="slide-index" style={{color:'var(--yd)'}}><span></span> Components</div>
          <h2 className="heading">
            Industrial <em>Components</em>
          </h2>
          <p className="comp-subtitle">Browse our catalog of high-quality industrial components, remanufactured parts, and machinery equipment.</p>
        </div>

        {/* Filters */}
        <div className="comp-filters rv">
          <div className="comp-search-wrap">
            <svg className="comp-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              type="text"
              className="comp-search-plain"
              placeholder="Search components..."
              value={filters.search}
              onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
            />
          </div>
          <select
            className="comp-select-plain"
            value={filters.brand}
            onChange={(e) => { setFilters({ ...filters, brand: e.target.value }); setPage(1); }}
          >
            <option value="">All Brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.slug}>{b.name}</option>
            ))}
          </select>
        </div>

        {/* Results count */}
        <div className="comp-meta rv">
          <span>{meta.total || products.length} component{(meta.total || products.length) !== 1 ? 's' : ''} found</span>
        </div>

        {/* Grid */}
        {loading && products.length === 0 ? (
          <div className="comp-loading">Loading...</div>
        ) : products.length > 0 ? (
          <div className="prod-grid">
            {products.map((p, i) => (
              <div className={`prod-card rv d${(i % 3) + 1}`} key={p.id || i}>
                <div className="pc-img-wrap">
                  <span className="pc-badge-stock">Stock: {p.stock}</span>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="pc-img" />
                  ) : (
                    <div className={`pc-img ${p.bg}`}>{p.icon}</div>
                  )}
                  <div className="pc-overlay"><a href="/contact" onClick={(e) => handleInquire(e, p)}>Inquire Now ↗</a><a href={`/components/${p.slug}`}>Detail</a></div>
                </div>
                <div className="pc-body">
                  <span className="pc-cat">{p.cat}</span>
                  <div className="pc-name">{p.name}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="comp-empty rv">
            <p>No components found matching your criteria.</p>
          </div>
        )}

        {/* Pagination */}
        {meta.last_page > 1 && (
          <div className="comp-pagination rv">
            <button
              className="comp-page-btn"
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              ← Prev
            </button>
            <div className="comp-page-numbers">
              {Array.from({ length: meta.last_page }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  className={`comp-page-num ${p === page ? 'active' : ''}`}
                  onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  {p}
                </button>
              ))}
            </div>
            <button
              className="comp-page-btn"
              disabled={page >= meta.last_page}
              onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Components;
